import {Tr} from "@/i18n/i18n";
import {VerticalStandardPanel} from "@/scenes.ui/vertical-standard-panel";
import {ResourceType} from "@/store/place/types";
import ResourceUtils from "@/utils/resource-utils";
import {Player} from "@/store/player/types";
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import {Keys} from "@/constants/key";
import {SquareButton} from "@/scenes.ui/square-button";
import BaseSound = Phaser.Sound.BaseSound;
import Container = Phaser.GameObjects.Container;
import Image = Phaser.GameObjects.Image;
import Text = Phaser.GameObjects.Text;
import {FeedbackPanel} from "@/scenes.ui/feedback-panel";
import {ExchangePortion, ExchangeAmount, ExchangeTerms, ExchangePrice} from "@/store/exchange/types";
import SoundUtils from "@/utils/sound-utils";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "@/utils/c-u";
import SContainer from "@/cmp/s-container";

export class ExchangePanel extends VerticalStandardPanel {

    private selectSound!: BaseSound | undefined;

    private mainContainer!: Container;

    private exchangeDto!: ExchangeTerms;
    private resources: Map<ResourceType, number> = new Map<ResourceType, number>();

    private redCrystalTab!: ExchangeTab;
    private blueCrystalTab!: ExchangeTab;

    private convertedAmountByType: Map<ResourceType, number> = new Map<ResourceType, number>();

    private convertedAmountText!: Text;
    private convertedAmountIcon!: Image;

    private convertationTypeByText: Map<ResourceType, Text> = new Map<ResourceType, Phaser.GameObjects.Text>();
    private convertationPercentByText: Map<ResourceType, Text> = new Map<ResourceType, Phaser.GameObjects.Text>();

    private convertedAmountTotal: number = 0;

    private confirmCallback!: (exchangeDtoFrom: ExchangeAmount) => void;

    constructor(scene: Phaser.Scene, canvas: HTMLCanvasElement, player: Player, exchangeDto: ExchangeTerms, confirmCallback: (exchangeDtoFrom: ExchangeAmount) => void) {
        super(scene, canvas, Tr('rinok'));

        this.exchangeDto = exchangeDto;
        this.confirmCallback = confirmCallback;

        this.selectSound = SoundUtils.addSound(scene, Keys.SOUND_SELECT);

        this.resources = ResourceUtils.convertToBalance(player);

        this.resetActiveTab();

        let y = -100;

        const exchangeTabs: ExchangeTab[] = [];

        this.redCrystalTab = new ExchangeTab(scene, canvas, -55, y, false, ResourceType.CRYSTAL, this.exchangeDto,
            (x: number, y: number, targetType: ResourceType, exchangePrices: ExchangePrice[]) => {
                SoundUtils.play(this.selectSound);
                this.resetActiveTab();
                exchangeTabs.forEach(st => st.setSelected(false));
                this.redCrystalTab.setSelected(true);
                this.populateTab(x, -190, targetType, exchangePrices);
            });

        this.blueCrystalTab = new ExchangeTab(scene, canvas, 55, y, false, ResourceType.ELIXIR, this.exchangeDto,
            (x: number, y: number, targetType: ResourceType, exchangePrices: ExchangePrice[]) => {
                SoundUtils.play(this.selectSound);
                this.resetActiveTab();
                exchangeTabs.forEach(st => st.setSelected(false));
                this.blueCrystalTab.setSelected(true);
                this.populateTab(x, -190, targetType, exchangePrices);
            });

        exchangeTabs.push(this.redCrystalTab);
        exchangeTabs.push(this.blueCrystalTab);

        this.blueCrystalTab.select();

        let separator = SpriteUtils.addSprite(this.scene!, 0, -175, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_SEPARATOR_LARGE);
        SpriteUtils.setDispSize(separator, 440 * CU.S, 10 * CU.S);

        this.add([separator, ...exchangeTabs]);

    }

    private populateTab(x: number, y: number, targetType: ResourceType, exchangePrices: ExchangePrice[]) {

        const mainResIcon = SpriteUtils.addImage(this.scene, -170, y + 55, ResourceUtils.getResourceImageId(targetType));
        SpriteUtils.setDispSize(mainResIcon, 60, 60);

        const mainResText = TextUtils.addTextAdvanced(this.scene, Tr('kupit') + ' ' + ResourceUtils.getResourceText(targetType).toUpperCase(), mainResIcon.getRightCenter().x! / CU.S + 10, mainResIcon.getRightCenter().y! / CU.S,
              '#eec761', 15, 'normal', 350, 'left', 2, Fonts.MYRIADPRO_BOLD);

        const arrow = SpriteUtils.addImage(this.scene, 0, 75, Keys.ATLAS_PROFILE, Keys.UI_ART_ARR);

        const giveText = TextUtils.addTextAdvanced(this.scene, Tr('otdaem'), arrow.x / CU.S - 72, arrow.y / CU.S + 40,
            '#929292', 11, 'normal', 350, 'center', 2, Fonts.MYRIADPRO_REGULAR);

        const getText = TextUtils.addTextAdvanced(this.scene, Tr('poluchaem'), arrow.x / CU.S + 75, arrow.y / CU.S + 40,
            '#929292', 11, 'normal', 350, 'center', 2, Fonts.MYRIADPRO_REGULAR);

        let offsetY = 15;

        exchangePrices.forEach(p => {

            this.fillConvertedByTypeMap(p.fromType, 0);

            const resText = TextUtils.addTextAdvanced(this.scene, Tr('za') + ResourceUtils.getResourceText(p.fromType).toLowerCase(), -200,
                mainResIcon.getBottomCenter().y! / CU.S + offsetY,
                '#eec761', 14, 'normal', 350, 'left', 2, Fonts.MYRIADPRO_REGULAR);

            const priceInfo = TextUtils.addTextAdvanced(this.scene, Tr('tsena.za.edinitsu'), resText.x / CU.S, resText.getBottomCenter().y! / CU.S + 15,
                '#929292', 11, 'normal', 150, 'left', 2, Fonts.MYRIADPRO_REGULAR);

            const priceText = TextUtils.addTextAdvanced(this.scene, p.priceForOne.toString(), priceInfo.getRightCenter().x! / CU.S + 10, priceInfo.y / CU.S,
                '#ffdfdf', 13, 'normal', 150, 'left', 2, Fonts.MYRIADPRO_BOLD);

            const resIcon = SpriteUtils.addImage(this.scene, priceText.getRightCenter().x! / CU.S + 15, priceText.y / CU.S, ResourceUtils.getResourceImageId(p.fromType));
            SpriteUtils.setDispSize(resIcon, 30, 30);

            this.addActionButtons(80, resText.getTopRight().y! / CU.S, targetType, p.fromType);

            this.addConvertingInfo(arrow.getLeftCenter().x! / CU.S - 30, arrow.getTopLeft().y! / CU.S + offsetY / 2 - 5, p.fromType);

            let percentText = TextUtils.addTextAdvanced(this.scene, '0%', 126, resText.getCenter().y! / CU.S + 17,
                '#c7c7c7', 12, 'normal', 150, 'center', 2, Fonts.MYRIADPRO_REGULAR);

            this.convertationPercentByText.set(p.fromType, percentText);

            let separator = SpriteUtils.addSprite(this.scene, 0, arrow.y / CU.S - 40, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_SEPARATOR, 0.6);

            this.mainContainer.add([resText, priceInfo, priceText, resIcon, separator, percentText]);

            offsetY += 70;
        });

        this.addAcceptButton(arrow.x, arrow.getBottomCenter().y! / CU.S + 98, targetType);

        this.convertedAmountText = TextUtils.addTextAdvanced(this.scene, this.convertedAmountTotal.toString(),
            arrow.getRightCenter().x! / CU.S + 30, arrow.y / CU.S,
            '#ffdfdf', 15, 'normal', 150, 'right', 2, Fonts.MYRIADPRO_BOLD);

        this.convertedAmountIcon = SpriteUtils.addImage(this.scene, this.convertedAmountText.getRightCenter().x! / CU.S + 15, this.convertedAmountText.y / CU.S,
            ResourceUtils.getResourceImageId(targetType));
        SpriteUtils.setDispSize(this.convertedAmountIcon, 30 * CU.S, 30 * CU.S);

        this.mainContainer.add([mainResIcon, mainResText, arrow, giveText, getText, this.convertedAmountText, this.convertedAmountIcon]);
    }

    private addActionButtons(x: number, y: number, targetType: ResourceType, fromType: ResourceType) {

        const minusButt = new SquareButton(this.scene, x, y + 27, 0, Keys.UI_ART_GREEN_BUTTON,
            Keys.ATLAS_UI, Keys.UI_WHITE_MINUS, '', false, 40, 40);
        SpriteUtils.scale(minusButt, 0.35 / CU.S);

        minusButt.setCallback(() => {
            if (this.isOnTop()) {
                SoundUtils.play(this.selectSound);
                this.changeConvertedAmount(targetType, fromType, CalculationType.DECREASE);
            }
            this.refreshPlusMinusVisibility(plusButt, minusButt, targetType, fromType);
        });

        const plusButt = new SquareButton(this.scene, minusButt.x / CU.S + 90, minusButt.y / CU.S, 0, Keys.UI_ART_GREEN_BUTTON,
            Keys.ATLAS_UI, Keys.UI_WHITE_PLUS, '', false, 40, 40);
        SpriteUtils.scale(plusButt, 0.35 / CU.S);

        plusButt.setCallback(() => {
            if (this.isOnTop()) {
                SoundUtils.play(this.selectSound);
                this.changeConvertedAmount(targetType, fromType, CalculationType.INCREASE);
            }
            this.refreshPlusMinusVisibility(plusButt, minusButt, targetType, fromType);
        });

        this.mainContainer.add([minusButt, plusButt]);
        this.refreshPlusMinusVisibility(plusButt, minusButt, targetType, fromType);
    }

    private refreshPlusMinusVisibility(plusButt: Container, minusButt: Container, targetType: ResourceType, fromType: ResourceType) {
        let canInc = this.canConvertAmount(targetType, fromType, CalculationType.INCREASE);
        let canDec = this.canConvertAmount(targetType, fromType, CalculationType.DECREASE);
        plusButt.setAlpha(canInc ? 1 : 0.3);
        minusButt.setAlpha(canDec ? 1 : 0.3);
    }

    private addConvertingInfo(x: number, y: number, fromType: ResourceType) {

        const resIcon = SpriteUtils.addImage(this.scene, x - 30, y, ResourceUtils.getResourceImageId(fromType));
        SpriteUtils.setDispSize(resIcon, 30 * CU.S, 30 * CU.S);

        let resText = TextUtils.addTextAdvanced(this.scene, "0", resIcon.getLeftCenter().x! / CU.S + 30, resIcon.y / CU.S,
            '#ffdfdf', 15, 'normal', 150, 'left', 2, Fonts.MYRIADPRO_BOLD);

        this.convertationTypeByText.set(fromType, resText);

        this.mainContainer.add([resIcon, resText]);
    }

    private addAcceptButton(x: number, y: number, targetType: ResourceType) {

        let acceptButton = new SquareButton(this.scene, x, y, -13, Keys.UI_ART_GREEN_BUTTON,
            Keys.ATLAS_PROFILE, Keys.UI_GOLDEN_CHECK, Tr('obmenjat'), false, 0,0, 38);

        acceptButton.setCallback(() => {
            SoundUtils.play(this.selectSound);
            if (this.isOnTop() && this.convertedAmountTotal > 0) {
                if (this.confirmCallback != null) {
                    this.confirmCallback(this.convertToExchangeDto(targetType, this.convertedAmountTotal, this.convertedAmountByType));
                }
                this.onClose();
                this.close();

            } else {
                new FeedbackPanel(this.scene, Tr('nechego.obmenivat'), undefined, false).show();
            }
        });

        this.mainContainer.add(acceptButton);
    }

    private changeConvertedAmount(targetType: ResourceType, resourceFrom: ResourceType, calcType: CalculationType) {
        return this.processConvertedAmount(targetType, resourceFrom, calcType, (takingCurrencyQuantity: number, takingResPriceForAll: number) => {
            this.doChangeAmount(takingCurrencyQuantity, resourceFrom, takingResPriceForAll);
        }, () => {
            if (calcType === CalculationType.INCREASE) {
                new FeedbackPanel(this.scene, Tr('nedostatochno.resursov'), undefined, false).show();
            }
        });
    }

    private canConvertAmount(targetType: ResourceType, resourceFrom: ResourceType, calcType: CalculationType) {
        return this.processConvertedAmount(targetType, resourceFrom, calcType, (takingCurrencyQuantity: number, takingResPriceForAll: number) => {
            return true;
        }, () => {
            return false;
        });
    }

    private processConvertedAmount(targetType: ResourceType, resourceFrom: ResourceType, calcType: CalculationType,
                    successCallback: (takingCurrencyQuantity: number, takingResPriceForAll: number) => unknown, failCallback: () => unknown) {

        // Всего ресов снимаемого типа
        const takingResTotal = this.resources.get(resourceFrom);

        // Цена за штуку желаемого ресурса
        const pricePerUnit = this.exchangeDto.exchangePrices.filter(p => p.targetType == targetType && p.fromType == resourceFrom).shift()!.priceForOne;

        // Сколько всего ресов снимаем за раз (процент от максимального). Округляем вверх
        const takingCurrencyQuantity = Math.ceil(takingResTotal! * (this.exchangeDto.percentageOfTotal / pricePerUnit / 100));

        // Цена за все забираемые ресурсы за клик
        const takingResPriceForAll = takingCurrencyQuantity * pricePerUnit;

        const convertedResQuantity = this.getConvertedAmountByType(resourceFrom);

        switch (calcType) {
            case CalculationType.INCREASE:
                if (this.checkIfPossibleToConvertTo(takingResTotal!, convertedResQuantity + takingResPriceForAll)) {
                    return successCallback(takingCurrencyQuantity, takingResPriceForAll);
                }
                break;
            case CalculationType.DECREASE:
                if (this.checkIfPossibleToConvertFrom(this.convertedAmountTotal,
                    -takingCurrencyQuantity, convertedResQuantity)) {
                    return successCallback(-takingCurrencyQuantity, -takingResPriceForAll);
                }
                break;
        }

        return failCallback();
    }

    private checkIfPossibleToConvertTo(takingResTotal: number, takingCurrencyQuantity: number): boolean {
        if (takingResTotal >= takingCurrencyQuantity) {
            return true;
        } else {
            return false;
        }

    }

    private checkIfPossibleToConvertFrom(convertedTotalIntoTarget: number, takingCurrencyQuantity: number,
                                        convertedByType: number): boolean {

        return convertedTotalIntoTarget - takingCurrencyQuantity >= 0 && convertedByType > 0;
    }

    private doChangeAmount(finalTakingCurrencyQuantity: number, resourceFrom: ResourceType, takingResPriceForAll: number) {
        let convertedAmount = this.getConvertedAmountByType(resourceFrom) + takingResPriceForAll;
        this.convertedAmountTotal += finalTakingCurrencyQuantity;
        this.convertedAmountByType.set(resourceFrom, convertedAmount);
        this.convertedAmountText.setText(TextUtils.formatMoney(this.convertedAmountTotal));
        this.convertedAmountIcon.setPosition(this.convertedAmountText.getRightCenter().x! + 15, this.convertedAmountText.y);
        this.convertationTypeByText.get(resourceFrom)!.setText(TextUtils.formatMoney(this.getConvertedAmountByType(resourceFrom)));

        let percent = 0;
        let totalAmount = this.resources.get(resourceFrom);
        if (totalAmount && totalAmount > 0) {
            percent = Math.floor(100 * this.getConvertedAmountByType(resourceFrom) / totalAmount);
        }
        this.convertationPercentByText.get(resourceFrom)!.setText(percent.toString() + '%');
    }

    private fillConvertedByTypeMap(type: ResourceType, amount: number) {
        this.convertedAmountByType.set(type, amount);
    }

    private getConvertedAmountByType(type: ResourceType): number {
        return this.convertedAmountByType.get(type)!;
    }

    private resetActiveTab() {
        if (this.mainContainer) {
            this.mainContainer.setVisible(false);
            this.mainContainer.destroy();
        }
        this.mainContainer = new Container(this.scene);
        this.add(this.mainContainer);

        this.convertedAmountByType.clear();

        this.convertedAmountTotal = 0;
    }

    private convertToExchangeDto(targetType: ResourceType, amount: number, convertedAmountByType: Map<ResourceType, number>): ExchangeAmount {

        let exchangeDto = new ExchangeAmount(targetType, amount);

        convertedAmountByType.forEach((value, key) => {
            exchangeDto.exchangePortions.push(new ExchangePortion(key, value));
        });


        return exchangeDto;
    }

}

export class ExchangeTab extends SContainer {

    private callback!: (x: number, y: number, targetType: ResourceType, exchangePrices: ExchangePrice[]) => void;

    private targetType!: ResourceType;
    private exchangePrices!: ExchangePrice[];

    private tabImage!: Image;

    constructor(scene: Phaser.Scene, canvas: HTMLCanvasElement, x: number, y: number, selected: boolean, targetType: ResourceType, exchangeDto: ExchangeTerms,
                callback: (x: number, y: number, targetType: ResourceType, exchangePrices: ExchangePrice[]) => void) {
        super(scene, x, y);

        this.targetType = targetType;

        this.callback = callback;

        this.exchangePrices = exchangeDto.exchangePrices.filter(p => p.targetType === targetType);

        this.tabImage = SpriteUtils.addImage(scene, x, y, Keys.ATLAS_SHOP_UI, selected ? Keys.UI_SHOP_TAB_ACTIVE : Keys.UI_SHOP_TAB_INACTIVE)
            .setInteractive();

        SpriteUtils.setDispSize(this.tabImage, this.getWidth(targetType)! * CU.S, 50 * CU.S);

        this.tabImage.on('pointerup', () => {
            if (this.callback != null) {
                this.callback(this.x, this.y, this.targetType, this.exchangePrices);
            }
        });

        let tabText = TextUtils.addTextByFamily(this.scene, ResourceUtils.getResourceText(targetType), this.tabImage.x / CU.S, this.tabImage.y / CU.S, '#ffffff', 13,
            'normal', 420, Fonts.ARIAL_MT);

        this.add([this.tabImage, tabText]);

    }


    public select() {
        if (this.callback != null) {
            this.callback(this.x, this.y, this.targetType, this.exchangePrices);
        }
    }

    public setSelected(selected: boolean): void {
        SpriteUtils.setSprite(this.scene, this.tabImage, Keys.ATLAS_SHOP_UI, selected ? Keys.UI_SHOP_TAB_ACTIVE : Keys.UI_SHOP_TAB_INACTIVE);
    }

    private getWidth(targetType: ResourceType) {
        return 210;
    }

}

export enum CalculationType {
    INCREASE = "INCREASE",
    DECREASE = "DECREASE"
}
